/**
 * `<KindGlyph>` — the one flat per-kind silhouette shared by `RolodexApp` (a 38px portrait-cell
 * fallback and a 92px card-head fallback) and `RolodexSpine` (a 22px row glyph). Its own file so
 * the two don't create a circular import between them (`RolodexSpine` needs it, `RolodexApp`
 * mounts `RolodexSpine`) and so both draw the SAME mark rather than two copies that could drift.
 *
 * `GLYPH` is a `Record<IndexKind, …>`, so a new kind fails typecheck here until it gets a mark.
 */
import type { ReactNode } from "react";
import type { IndexKind } from "./codexView";

const GLYPH: Record<IndexKind, ReactNode> = {
  // A lancer is a person in the accent colour — the same figure, marked as one of the table's own.
  lancer: (
    <>
      <circle cx="16" cy="11" r="6" fill="var(--gms-accent)" />
      <path d="M4 31 C4 22 10 18.5 16 18.5 C22 18.5 28 22 28 31 Z" fill="var(--gms-accent)" />
    </>
  ),
  person: (
    <>
      <circle cx="16" cy="11" r="6" fill="var(--gms-ink)" />
      <path d="M4 31 C4 22 10 18.5 16 18.5 C22 18.5 28 22 28 31 Z" fill="var(--gms-ink)" />
    </>
  ),
  place: (
    <>
      <circle cx="16" cy="16" r="9" fill="var(--gms-ink)" />
      <ellipse cx="16" cy="16" rx="15" ry="4.5" fill="none" stroke="var(--gms-accent)" strokeWidth="2" />
    </>
  ),
  faction: (
    <>
      <path d="M16 2 L28 7 V17 C28 24 22 28.5 16 30.5 C10 28.5 4 24 4 17 V7 Z" fill="var(--gms-ink)" />
      <path d="M16 9 L21 18 H11 Z" fill="var(--gms-accent)" />
    </>
  ),
  ship: (
    <>
      <path d="M2 20 L16 4 L30 20 L16 26 Z" fill="var(--gms-ink)" />
      <rect x="12" y="26" width="8" height="4" fill="var(--gms-accent)" />
    </>
  ),
  // An `org` is an institution, not a power — a colonnade rather than the faction's
  // shield, so the two read as different things at a glance.
  org: (
    <>
      <path d="M16 2 L30 11 H2 Z" fill="var(--gms-accent)" />
      <rect x="6" y="13" width="4" height="13" fill="var(--gms-ink)" />
      <rect x="14" y="13" width="4" height="13" fill="var(--gms-ink)" />
      <rect x="22" y="13" width="4" height="13" fill="var(--gms-ink)" />
      <rect x="3" y="26" width="26" height="4" fill="var(--gms-ink)" />
    </>
  ),
};

export function KindGlyph({ kind }: { kind: IndexKind }) {
  return (
    <svg viewBox="0 0 32 32" aria-hidden role="img" className="rlx-glyph">
      {GLYPH[kind]}
    </svg>
  );
}
